/**
 * FormConcierge-Markdown — the strict renderer for everything the plansheet pipeline sends back.
 *
 * Every upstream string is untrusted (contract §6 + D4): assistant AND user turns, standing instructions,
 * metadata, form / request / file names. A prompt injected through an uploaded file can steer a reply, so
 * this renderer is stricter than `_UserMarkdown.js`:
 *
 * - raw HTML is shown as escaped text, block and inline, including inside table cells and link text;
 * - no `<img>` is ever emitted (an https image becomes a link labelled with its alt text, others its alt text);
 * - links are made only for absolute https URLs that are not Headlight, the current host, localhost or an IP
 *   (a `/1.0/Report/<guid>/index.html` render URL on a Headlight API host other than the current host is the
 *   one exception), and every link shows its destination host beside it;
 * - a delivery command is offered for copy only when it has the exact shape `cd <dir> && npx hldi load …`.
 *
 * marked: a require from this folder resolves pict-section-form's marked 4.3, which has no `Marked` class
 * and positional renderer arguments, so the root copy (14.x, also at /working/node_modules in Docker) is
 * loaded by path and used only through a private instance (never `marked.use()` / `setOptions()`).
 *
 * No window / document access at load time.
 */

const libUserMarkdown = require('../../../../lib/web/headlight-dynamic-inputs-bundle/source/providers/_UserMarkdown.js');

/** @type {any} */
let libMarked = null;
try
{
	libMarked = require('../../../../node_modules/marked');
}
catch (pError)
{
	// Reported at first render, where the fallback to escaped text already handles it.
	libMarked = null;
}

/** Façade-added steering starts here; everything from it on is not the user's text (contract C1). */
const STEERING_MARKER = '\n\n---\nForm Concierge steering:\n';
/** Leading paragraph the façade puts on an examples turn. */
const EXAMPLES_PREFIX = '[examples] ';
/** Leading paragraph the façade puts on an update (revision) turn. */
const REVISION_PREFIX = '[revision] ';

/** Example-document render URLs, the one Headlight path a reply may link to (D2). */
const REPORT_PATH_PATTERN = /^\/1\.0\/Report\/[0-9A-Za-z-]+\/index\.html$/;
/** The Headlight hosts a render URL may point at: the API hosts only (api.headlight.com, api.qa.headlight.com, …). */
const REPORT_HOST_PATTERN = /^api(\.[a-z0-9-]+)*\.headlight\.com$/;
/** Characters that never belong in a copyable command (D3). */
const COMMAND_FORBIDDEN_PATTERN = /[;|$`<>()]/;
/** The fixed head of a copyable command; the cd path cannot contain a space, so this match is linear. */
const COMMAND_HEAD_PATTERN = /^cd [\w./~-]+ && npx hldi load /;
/** Longest command considered for copy. */
const COMMAND_MAX_LENGTH = 2000;
/** Longest JSON shown in a metadata Details block. */
const DETAILS_MAX_LENGTH = 20000;

/** @type {Record<string, string>} */
const _TextEntityMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' };
/** Unescaped specials, leaving existing entity references alone (so applying it twice is harmless). */
const _TextEscapePattern = /[<>"']|&(?!(?:#\d{1,7}|#[Xx][a-fA-F0-9]{1,6}|\w+);)/g;

/** @type {any} Built on first render. */
let _RemoteMarked = null;
/** Options of the render in progress (marked parses synchronously, so one slot is enough). */
let _ActiveOptions = {};
/** How many links the render is inside, so an image in link text renders as its alt only. */
let _LinkDepth = 0;

/**
 * Escapes a value for HTML text or an attribute (the shared `_UserMarkdown.js` rules).
 *
 * @param {any} pValue - the value to escape.
 *
 * @return {string} the escaped value; '' for null/undefined.
 */
function escapeHTML(pValue)
{
	return libUserMarkdown.escapeHTML(pValue);
}

/**
 * Escapes the specials in text that may already be escaped, without double-escaping entities.
 *
 * @param {any} pText - text from a marked token.
 *
 * @return {string} safe HTML text.
 */
function _escapeTextIdempotent(pText)
{
	return String((pText === null) || (typeof pText === 'undefined') ? '' : pText)
		.replace(_TextEscapePattern, (pCharacter) => _TextEntityMap[pCharacter]);
}

/**
 * Lower-cases a host name and drops a trailing dot and any port, so 'Config.Headlight.com.:443' compares as
 * 'config.headlight.com'.
 *
 * @param {any} pHost - a host or hostname.
 *
 * @return {string} the normalized hostname, or ''.
 */
function _normalizeHost(pHost)
{
	let tmpHost = (typeof pHost === 'string') ? pHost.trim().toLowerCase() : '';
	if (!tmpHost)
	{
		return '';
	}
	if (tmpHost.charAt(0) !== '[')
	{
		const tmpColon = tmpHost.indexOf(':');
		if (tmpColon >= 0)
		{
			tmpHost = tmpHost.slice(0, tmpColon);
		}
	}
	// A loop, not /\.+$/ (quadratic on a long run of dots).
	let tmpEnd = tmpHost.length;
	while ((tmpEnd > 0) && (tmpHost.charAt(tmpEnd - 1) === '.'))
	{
		tmpEnd--;
	}
	return tmpHost.slice(0, tmpEnd);
}

/**
 * The host the page is served from: the option when given, else `location.hostname` (read at call time).
 *
 * @param {any} pOptions - render options.
 *
 * @return {string} the normalized current host, or ''.
 */
function _currentHost(pOptions)
{
	if (pOptions && (typeof pOptions.currentHost === 'string'))
	{
		return _normalizeHost(pOptions.currentHost);
	}
	try
	{
		if ((typeof location !== 'undefined') && location && location.hostname)
		{
			return _normalizeHost(location.hostname);
		}
	}
	catch (pError)
	{
		return '';
	}
	return '';
}

/**
 * Whether a parsed hostname is an IP literal. The URL parser has already turned every IPv4 spelling
 * (hex, octal, a bare number) into a dotted quad, and a host whose last label is all digits is never a name.
 *
 * @param {string} pHostname - a normalized hostname.
 *
 * @return {boolean} true for IPv4 / IPv6 literals.
 */
function _isIPLiteral(pHostname)
{
	if (pHostname.charAt(0) === '[')
	{
		return true;
	}
	const tmpLabels = pHostname.split('.');
	return /^(?:0x[0-9a-f]*|\d+)$/i.test(tmpLabels[tmpLabels.length - 1]);
}

/**
 * Whether a hostname is Headlight's (headlight.com or any subdomain), where `/1.0/*` reaches the API with the
 * viewer's session cookie.
 *
 * @param {string} pHostname - a normalized hostname.
 *
 * @return {boolean} true for Headlight hosts.
 */
function _isHeadlightHost(pHostname)
{
	return (pHostname === 'headlight.com') || /\.headlight\.com$/.test(pHostname);
}

/**
 * The link policy (D2): returns the parsed URL when it may be linked, else null.
 *
 * Only absolute https URLs without credentials, and never Headlight, the current host, localhost or an IP
 * literal. The current host is checked first, so no exception reaches it. With `allowReport`, a Headlight
 * API host (`api[.<env>].headlight.com`) is allowed for exactly `/1.0/Report/<guid>/index.html` with no
 * query or fragment.
 *
 * @param {any} pHref - the candidate href.
 * @param {{ currentHost?: string, allowReport?: boolean }} [pOptions] - policy options.
 *
 * @return {URL|null} the URL to link to (emit its `href`), or null to render text.
 */
function safeLink(pHref, pOptions)
{
	const tmpOptions = pOptions || {};
	if ((typeof pHref !== 'string') || (typeof URL !== 'function'))
	{
		return null;
	}
	const tmpHref = pHref.trim();
	if (!tmpHref)
	{
		return null;
	}
	let tmpURL;
	try
	{
		// No base: relative, protocol-relative and /1.0/... hrefs throw here.
		tmpURL = new URL(tmpHref);
	}
	catch (pError)
	{
		return null;
	}
	if ((tmpURL.protocol !== 'https:') || tmpURL.username || tmpURL.password)
	{
		return null;
	}
	const tmpHostname = _normalizeHost(tmpURL.hostname);
	if (!tmpHostname || _isIPLiteral(tmpHostname))
	{
		return null;
	}
	if ((tmpHostname === 'localhost') || /\.localhost$/.test(tmpHostname))
	{
		return null;
	}
	// The page's own origin first, before any exception: a link there runs with the viewer's session on
	// the façade's origin (Kong serves /1.0 on every host), so it is never made.
	const tmpCurrentHost = _currentHost(tmpOptions);
	if (tmpCurrentHost && (tmpHostname === tmpCurrentHost))
	{
		return null;
	}
	if (_isHeadlightHost(tmpHostname))
	{
		// The render-URL exception, for the API hosts only; tmpURL.pathname is already dot-segment normalized.
		if (tmpOptions.allowReport && REPORT_HOST_PATTERN.test(tmpHostname) && REPORT_PATH_PATTERN.test(tmpURL.pathname) && !tmpURL.search && !tmpURL.hash && (tmpHref.indexOf('?') < 0) && (tmpHref.indexOf('#') < 0))
		{
			return tmpURL;
		}
		return null;
	}
	return tmpURL;
}

/**
 * A link that opens in a new tab and shows its destination host, or just the label when the href fails the
 * link policy.
 *
 * @param {any} pHref - the candidate href.
 * @param {string} pLabelHTML - the link text, ALREADY safe HTML (escaped by the caller or rendered by marked).
 * @param {{ currentHost?: string, allowReport?: boolean }} [pOptions] - policy options.
 *
 * @return {string} '<a …>label</a><span class="fcg-linkhost">(host)</span>' or the label alone.
 */
function linkHTML(pHref, pLabelHTML, pOptions)
{
	const tmpLabel = (typeof pLabelHTML === 'string') ? pLabelHTML : '';
	const tmpURL = safeLink(pHref, pOptions);
	if (!tmpURL)
	{
		return tmpLabel;
	}
	const tmpShown = tmpLabel.trim() ? tmpLabel : escapeHTML(tmpURL.href);
	return `<a href="${ escapeHTML(tmpURL.href) }" target="_blank" rel="noopener noreferrer nofollow">${ tmpShown }</a><span class="fcg-linkhost">(${ escapeHTML(tmpURL.host) })</span>`;
}

/**
 * The private marked instance (marked >= 13 object-argument renderers).
 *
 * @return {any} the Marked instance.
 */
function _getRemoteMarked()
{
	if (_RemoteMarked)
	{
		return _RemoteMarked;
	}
	if (!libMarked || (typeof libMarked.Marked !== 'function'))
	{
		throw new Error('FormConcierge needs marked >= 13');
	}
	_RemoteMarked = new libMarked.Marked(
		{
			gfm: true,
			breaks: true,
			async: false,
			// Inline text after an inline <pre>/<code>/<kbd>/<script> tag reaches the renderer unescaped
			// (marked's "raw block" state), so `<kbd><img/src=x onerror=…>` would otherwise run. Escape every
			// leaf text token; already-escaped text is left as it is.
			walkTokens: function(pToken)
			{
				if (pToken && (pToken.type === 'text') && !pToken.tokens && (typeof pToken.text === 'string'))
				{
					pToken.text = _escapeTextIdempotent(pToken.text);
				}
			},
			renderer:
			{
				// Function expressions: `this` is marked's renderer, which carries `this.parser`.
				html: function(pToken)
				{
					const tmpText = escapeHTML(pToken.text);
					return pToken.block ? `<pre class="fcg-md-raw">${ tmpText }</pre>\n` : tmpText;
				},
				link: function(pToken)
				{
					let tmpLabel = '';
					_LinkDepth++;
					try
					{
						tmpLabel = this.parser.parseInline(pToken.tokens);
					}
					finally
					{
						_LinkDepth--;
					}
					let tmpHref = pToken.href;
					// A <https://…> autolink carries an HTML-escaped href; its raw text is the real one.
					if ((typeof pToken.raw === 'string') && /^<[^<>]*>$/.test(pToken.raw))
					{
						tmpHref = pToken.raw.slice(1, -1);
					}
					return linkHTML(tmpHref, tmpLabel, _ActiveOptions);
				},
				image: function(pToken)
				{
					// The alt text arrives escaped.
					const tmpAlt = (typeof pToken.text === 'string') ? pToken.text : '';
					if (_LinkDepth > 0)
					{
						return tmpAlt;
					}
					const tmpURL = safeLink(pToken.href, _ActiveOptions);
					if (!tmpURL)
					{
						return tmpAlt;
					}
					return linkHTML(tmpURL.href, tmpAlt.trim() ? tmpAlt : 'image', _ActiveOptions);
				},
			},
		});
	return _RemoteMarked;
}

/**
 * Renders untrusted remote markdown to display HTML (contract §6, D1, D2, D4). Falls back to the escaped
 * source in a `<pre>` when marked is unusable or throws.
 *
 * @param {any} pMarkdown - the markdown.
 * @param {{ currentHost?: string, allowReport?: boolean }} [pOptions] - link policy; allowReport defaults to true.
 *
 * @return {string} HTML.
 */
function renderRemoteMarkdown(pMarkdown, pOptions)
{
	if ((pMarkdown === null) || (typeof pMarkdown === 'undefined') || (pMarkdown === ''))
	{
		return '';
	}
	const tmpMarkdown = (typeof pMarkdown === 'string') ? pMarkdown : String(pMarkdown);
	const tmpSource = pOptions || {};
	const tmpOptions =
		{
			currentHost: _currentHost(tmpSource),
			allowReport: (typeof tmpSource.allowReport === 'boolean') ? tmpSource.allowReport : true,
		};
	try
	{
		_ActiveOptions = tmpOptions;
		_LinkDepth = 0;
		return String(_getRemoteMarked().parse(tmpMarkdown));
	}
	catch (pError)
	{
		return `<pre class="fcg-md-fallback">${ escapeHTML(tmpMarkdown) }</pre>`;
	}
	finally
	{
		_ActiveOptions = {};
		_LinkDepth = 0;
	}
}

/**
 * Whether the text after `… hldi load ` matches `[\w./ -]+( --[\w-]+( [\w.,/-]+)?)*$` — computed in linear
 * time, because that regex backtracks exponentially on crafted input (~20 s for under 200 characters) and the
 * command is attacker-steerable.
 *
 * A comma can only sit in an option value, so a tail without one only has to fit `[\w./ -]+`. Otherwise the
 * token holding the first comma is a value whose option is the token just before it; from that option on the
 * tail is single-space separated tokens of `[\w.,/-]+`, where every token that is not an option (`--[\w-]+`)
 * directly follows one that is.
 *
 * @param {string} pTail - the text after the fixed head.
 *
 * @return {boolean} true when the tail fits.
 */
function _commandTailMatches(pTail)
{
	if (!pTail || /[^\w./ ,-]/.test(pTail))
	{
		return false;
	}
	const tmpFirstComma = pTail.indexOf(',');
	if (tmpFirstComma < 0)
	{
		return true;
	}
	const tmpValueStart = pTail.lastIndexOf(' ', tmpFirstComma) + 1;
	if (tmpValueStart < 2)
	{
		return false;
	}
	const tmpOptionStart = pTail.lastIndexOf(' ', tmpValueStart - 2) + 1;
	// The option must be preceded by a space that is not the tail's first character (the head part is non-empty).
	if (tmpOptionStart < 2)
	{
		return false;
	}
	const tmpTokens = pTail.slice(tmpOptionStart).split(' ');
	const tmpIsOption = (pToken) => /^--[\w-]+$/.test(pToken);
	if (!tmpIsOption(tmpTokens[0]))
	{
		return false;
	}
	for (let i = 0; i < tmpTokens.length; i++)
	{
		if (!/^[\w.,/-]+$/.test(tmpTokens[i]))
		{
			return false;
		}
		if ((i > 0) && !tmpIsOption(tmpTokens[i]) && !tmpIsOption(tmpTokens[i - 1]))
		{
			return false;
		}
	}
	return true;
}

/**
 * Whether a delivery command may be offered for copy (D3): exactly
 * `^cd [\w./~-]+ && npx hldi load [\w./ -]+( --[\w-]+( [\w.,/-]+)?)*$`, none of `;|$\`<>()`, and no `&`
 * besides the single `&&`.
 *
 * @param {any} pCommand - the command from reply metadata.
 *
 * @return {boolean} true when it is safe to offer.
 */
function isCopyableCommand(pCommand)
{
	if ((typeof pCommand !== 'string') || !pCommand || (pCommand.length > COMMAND_MAX_LENGTH))
	{
		return false;
	}
	if (COMMAND_FORBIDDEN_PATTERN.test(pCommand) || (pCommand.split('&').length !== 3) || (pCommand.indexOf('&&') < 0))
	{
		return false;
	}
	const tmpHead = COMMAND_HEAD_PATTERN.exec(pCommand);
	if (!tmpHead)
	{
		return false;
	}
	return _commandTailMatches(pCommand.slice(tmpHead[0].length));
}

/**
 * The user's own words from a composed turn (C6): drops everything from STEERING_MARKER on, then a leading
 * EXAMPLES_PREFIX / REVISION_PREFIX paragraph (up to the first blank line). Must match the façade's copy.
 *
 * @param {any} pText - a composed input value or turn Content.
 *
 * @return {string} the stripped text.
 */
function stripSteering(pText)
{
	// Non-strings are '' (as in the façade's copy).
	let tmpText = (typeof pText === 'string') ? pText : '';
	const tmpMarker = tmpText.indexOf(STEERING_MARKER);
	if (tmpMarker >= 0)
	{
		tmpText = tmpText.slice(0, tmpMarker);
	}
	if ((tmpText.indexOf(EXAMPLES_PREFIX) === 0) || (tmpText.indexOf(REVISION_PREFIX) === 0))
	{
		const tmpBlank = tmpText.indexOf('\n\n');
		tmpText = (tmpBlank >= 0) ? tmpText.slice(tmpBlank + 2) : '';
	}
	return tmpText;
}

/**
 * A metadata value as chip text.
 *
 * @param {any} pValue - the value.
 *
 * @return {string} plain (unescaped) text; '' when there is nothing to show.
 */
function _chipText(pValue)
{
	if ((pValue === null) || (typeof pValue === 'undefined'))
	{
		return '';
	}
	if (typeof pValue === 'boolean')
	{
		return pValue ? 'yes' : 'no';
	}
	if ((typeof pValue === 'string') || (typeof pValue === 'number'))
	{
		return String(pValue);
	}
	try
	{
		return String(JSON.stringify(pValue)).slice(0, 300);
	}
	catch (pError)
	{
		return '';
	}
}

/**
 * One escaped label/value chip.
 *
 * @param {string} pLabel - fixed label text.
 * @param {any} pValue - the untrusted value.
 * @param {string} [pModifier] - optional fixed modifier class.
 *
 * @return {string} HTML, or '' for an empty value.
 */
function _chip(pLabel, pValue, pModifier)
{
	const tmpText = _chipText(pValue);
	if (!tmpText)
	{
		return '';
	}
	const tmpClass = pModifier ? `fcg-chip ${ pModifier }` : 'fcg-chip';
	return `<span class="${ tmpClass }"><span class="fcg-chip-label">${ escapeHTML(pLabel) }</span> <span class="fcg-chip-value">${ escapeHTML(tmpText) }</span></span> `;
}

/**
 * A count for fields that may arrive as a number, an array or an object.
 *
 * @param {any} pValue - the field value.
 *
 * @return {any} a number for arrays/objects, else the value.
 */
function _countOf(pValue)
{
	if (Array.isArray(pValue))
	{
		return pValue.length;
	}
	if (pValue && (typeof pValue === 'object'))
	{
		return Object.keys(pValue).length;
	}
	return pValue;
}

/**
 * Copies the keys of an object that a group did not render, so the Details block shows them.
 *
 * @param {Record<string, any>} pObject - the group object.
 * @param {Array<string>} pKnown - keys the group rendered.
 *
 * @return {Record<string, any>|null} the leftovers, or null when none.
 */
function _leftoverKeys(pObject, pKnown)
{
	/** @type {Record<string, any>} */
	const tmpLeftover = {};
	let tmpAny = false;
	Object.keys(pObject).forEach((pKey) =>
	{
		if (pKnown.indexOf(pKey) < 0)
		{
			tmpLeftover[pKey] = pObject[pKey];
			tmpAny = true;
		}
	});
	return tmpAny ? tmpLeftover : null;
}

/**
 * Whether a value is a plain (non-array) object.
 *
 * @param {any} pValue - the value.
 *
 * @return {boolean} true for objects.
 */
function _isObject(pValue)
{
	return !!pValue && (typeof pValue === 'object') && !Array.isArray(pValue);
}

/**
 * The parsed plansheet.io share URL, or null when the value is not one (D2 + §6: only https://plansheet.io/).
 *
 * @param {any} pShareURL - Blueprint.ShareURL.
 *
 * @return {URL|null} the URL.
 */
function _plansheetShareURL(pShareURL)
{
	if ((typeof pShareURL !== 'string') || (pShareURL.trim().indexOf('https://plansheet.io/') !== 0) || (typeof URL !== 'function'))
	{
		return null;
	}
	try
	{
		const tmpURL = new URL(pShareURL.trim());
		if ((tmpURL.protocol === 'https:') && (tmpURL.hostname === 'plansheet.io') && !tmpURL.port && !tmpURL.username && !tmpURL.password)
		{
			return tmpURL;
		}
	}
	catch (pError)
	{
		return null;
	}
	return null;
}

/**
 * Chips for reply metadata (§6 + D2/D3). Every value is escaped; links follow the link policy; a Copy
 * button appears only for a command that passes isCopyableCommand, as
 * `<button type="button" class="fcg-copy" data-fcg-copy="<index into Copyables>">` (no inline handlers).
 *
 * @param {any} pMeta - parsed MetadataJSON (a JSON string is parsed too), or null.
 * @param {{ currentHost?: string }} [pOptions] - link policy options.
 *
 * @return {{ HTML: string, Copyables: Array<string> }} the chips and the copyable strings.
 */
function renderReplyMetadata(pMeta, pOptions)
{
	/** @type {Array<string>} */
	const tmpCopyables = [];
	let tmpMeta = pMeta;
	if (typeof tmpMeta === 'string')
	{
		try
		{
			tmpMeta = tmpMeta.trim() ? JSON.parse(tmpMeta) : null;
		}
		catch (pError)
		{
			tmpMeta = { Unparsed: pMeta };
		}
	}
	if ((tmpMeta === null) || (typeof tmpMeta === 'undefined'))
	{
		return { HTML: '', Copyables: tmpCopyables };
	}
	if (!_isObject(tmpMeta))
	{
		tmpMeta = { Value: tmpMeta };
	}
	const tmpHostOptions = { currentHost: _currentHost(pOptions || {}) };
	/** @type {Record<string, any>} */
	const tmpLeftover = {};
	let tmpHasLeftover = false;
	const tmpGroups = [];
	const tmpHandled = [ 'Blueprint', 'HeadlightForm', 'Config', 'Session', 'Examples', 'Documents' ];

	/**
	 * Records a group's unrendered keys.
	 * @param {string} pKey - group key.
	 * @param {any} pValue - leftovers or null.
	 */
	const fKeep = (pKey, pValue) =>
	{
		if (pValue !== null)
		{
			tmpLeftover[pKey] = pValue;
			tmpHasLeftover = true;
		}
	};

	Object.keys(tmpMeta).forEach((pKey) =>
	{
		const tmpValue = tmpMeta[pKey];
		const tmpKnownShape = (pKey === 'Examples' || pKey === 'Documents') ? Array.isArray(tmpValue) : _isObject(tmpValue);
		if ((tmpHandled.indexOf(pKey) < 0) || !tmpKnownShape)
		{
			fKeep(pKey, tmpValue);
		}
	});

	const tmpBlueprint = tmpMeta.Blueprint;
	if (_isObject(tmpBlueprint))
	{
		let tmpHTML = '';
		if ((tmpBlueprint.BlueprintNumber !== null) && (typeof tmpBlueprint.BlueprintNumber !== 'undefined') && (tmpBlueprint.BlueprintNumber !== ''))
		{
			tmpHTML += _chip('Blueprint', `#${ _chipText(tmpBlueprint.BlueprintNumber) }`);
			tmpHTML += _chip('ID', tmpBlueprint.IDBlueprint);
		}
		else
		{
			tmpHTML += _chip('Blueprint', tmpBlueprint.IDBlueprint);
		}
		if ((typeof tmpBlueprint.ShareURL !== 'undefined') && (tmpBlueprint.ShareURL !== null) && (tmpBlueprint.ShareURL !== ''))
		{
			const tmpShareURL = _plansheetShareURL(tmpBlueprint.ShareURL);
			const tmpLink = tmpShareURL ? linkHTML(tmpShareURL.href, 'Open blueprint', tmpHostOptions) : '';
			tmpHTML += tmpLink ? `<span class="fcg-meta-link">${ tmpLink }</span>` : '<span class="fcg-meta-muted">Share link not shown (not a plansheet.io address)</span>';
		}
		tmpGroups.push(`<div class="fcg-meta-group fcg-meta-blueprint"><span class="fcg-meta-title">Blueprint</span> ${ tmpHTML }</div>`);
		fKeep('Blueprint', _leftoverKeys(tmpBlueprint, [ 'IDBlueprint', 'BlueprintNumber', 'ShareURL' ]));
	}

	const tmpForm = tmpMeta.HeadlightForm;
	if (_isObject(tmpForm))
	{
		let tmpHTML = '';
		tmpHTML += _chip('Hash', tmpForm.Hash);
		tmpHTML += _chip('Environment', tmpForm.Environment);
		if (tmpForm.Pushed === true)
		{
			tmpHTML += _chip('Pushed', 'yes', 'fcg-chip-ok');
		}
		else if (tmpForm.Pushed === false)
		{
			tmpHTML += _chip('Pushed', 'no', 'fcg-chip-warn');
		}
		else
		{
			tmpHTML += _chip('Pushed', tmpForm.Pushed);
		}
		tmpHTML += _chip('Customer', tmpForm.Customer);
		tmpHTML += _chip('Default report', tmpForm.DefaultReport);
		const tmpCommand = tmpForm.Command;
		if ((tmpCommand !== null) && (typeof tmpCommand !== 'undefined') && (tmpCommand !== ''))
		{
			if (isCopyableCommand(tmpCommand))
			{
				const tmpIndex = tmpCopyables.length;
				tmpCopyables.push(tmpCommand);
				tmpHTML += `<div class="fcg-command"><pre class="fcg-command-text">${ escapeHTML(tmpCommand) }</pre><button type="button" class="fcg-copy" data-fcg-copy="${ tmpIndex }">Copy</button></div>`;
			}
			else
			{
				tmpHTML += `<div class="fcg-command fcg-command-unrecognized"><div class="fcg-meta-muted">Unrecognized command — not offered for copy</div><pre class="fcg-command-text">${ escapeHTML(_chipText(tmpCommand)) }</pre></div>`;
			}
		}
		tmpGroups.push(`<div class="fcg-meta-group fcg-meta-headlightform"><span class="fcg-meta-title">Headlight form</span> ${ tmpHTML }</div>`);
		fKeep('HeadlightForm', _leftoverKeys(tmpForm, [ 'Hash', 'Environment', 'Pushed', 'Customer', 'DefaultReport', 'Command' ]));
	}

	const tmpConfig = tmpMeta.Config;
	if (_isObject(tmpConfig))
	{
		let tmpHTML = '';
		tmpHTML += _chip('Form hash', tmpConfig.FormHash);
		tmpHTML += _chip('Sections', _countOf(tmpConfig.Sections));
		tmpHTML += _chip('Descriptors', _countOf(tmpConfig.Descriptors));
		tmpHTML += _chip('Exact match', tmpConfig.ValidationExactRate);
		const tmpNotes = _chipText(tmpConfig.Notes);
		if (tmpNotes)
		{
			tmpHTML += `<div class="fcg-meta-notes">${ escapeHTML(tmpNotes).replace(/\r?\n/g, '<br>') }</div>`;
		}
		tmpGroups.push(`<div class="fcg-meta-group fcg-meta-config"><span class="fcg-meta-title">Configuration</span> ${ tmpHTML }</div>`);
		fKeep('Config', _leftoverKeys(tmpConfig, [ 'FormHash', 'Sections', 'Descriptors', 'ValidationExactRate', 'Notes' ]));
	}

	const tmpSession = tmpMeta.Session;
	if (_isObject(tmpSession))
	{
		tmpGroups.push(`<div class="fcg-meta-group fcg-meta-session"><span class="fcg-meta-title">Session</span> ${ _chip('Session file', tmpSession.IDSessionFile) }</div>`);
		fKeep('Session', _leftoverKeys(tmpSession, [ 'IDSessionFile' ]));
	}

	[ 'Examples', 'Documents' ].forEach((pKey) =>
	{
		const tmpList = tmpMeta[pKey];
		if (!Array.isArray(tmpList))
		{
			return;
		}
		let tmpRows = '';
		/** @type {Array<any>} */
		const tmpOther = [];
		tmpList.forEach((pRow) =>
		{
			if (!_isObject(pRow))
			{
				tmpOther.push(pRow);
				return;
			}
			const tmpName = _chipText(pRow.Name) || 'Document';
			const tmpReportOptions = { currentHost: tmpHostOptions.currentHost, allowReport: true };
			const tmpRenderURL = safeLink(pRow.RenderURL, tmpReportOptions);
			let tmpLink = '';
			if (tmpRenderURL)
			{
				tmpLink = `<span class="fcg-meta-link">${ linkHTML(tmpRenderURL.href, 'Open', tmpReportOptions) }</span>`;
			}
			else if ((pRow.RenderURL !== null) && (typeof pRow.RenderURL !== 'undefined') && (pRow.RenderURL !== ''))
			{
				tmpLink = '<span class="fcg-meta-muted">(render link not shown)</span>';
			}
			tmpRows += `<li><span class="fcg-meta-docname">${ escapeHTML(tmpName) }</span> ${ _chip('ID', pRow.IDDocument) }${ tmpLink }</li>`;
			// Rows with extra keys or a refused link go to Details too, so nothing is hidden.
			if (_leftoverKeys(pRow, [ 'Name', 'IDDocument', 'RenderURL' ]) || (tmpLink && !tmpRenderURL))
			{
				tmpOther.push(pRow);
			}
		});
		tmpGroups.push(`<div class="fcg-meta-group fcg-meta-documents"><span class="fcg-meta-title">${ (pKey === 'Examples') ? 'Examples' : 'Documents' }</span><ul class="fcg-meta-doclist">${ tmpRows }</ul></div>`);
		if (tmpOther.length)
		{
			fKeep(pKey, tmpOther);
		}
	});

	if (tmpHasLeftover)
	{
		let tmpJSON = '';
		try
		{
			tmpJSON = String(JSON.stringify(tmpLeftover, null, 2));
		}
		catch (pError)
		{
			tmpJSON = '(not displayable)';
		}
		if (tmpJSON.length > DETAILS_MAX_LENGTH)
		{
			tmpJSON = `${ tmpJSON.slice(0, DETAILS_MAX_LENGTH) }\n… (truncated)`;
		}
		tmpGroups.push(`<details class="fcg-meta-details"><summary>Details</summary><pre>${ escapeHTML(tmpJSON) }</pre></details>`);
	}

	return { HTML: tmpGroups.length ? `<div class="fcg-meta">${ tmpGroups.join('') }</div>` : '', Copyables: tmpCopyables };
}

module.exports =
{
	renderRemoteMarkdown: renderRemoteMarkdown,
	safeLink: safeLink,
	linkHTML: linkHTML,
	escapeHTML: escapeHTML,
	renderReplyMetadata: renderReplyMetadata,
	isCopyableCommand: isCopyableCommand,
	stripSteering: stripSteering,
	STEERING_MARKER: STEERING_MARKER,
	EXAMPLES_PREFIX: EXAMPLES_PREFIX,
	REVISION_PREFIX: REVISION_PREFIX,
};
